import React, { useEffect, useMemo, useState } from 'react';

import api from '../api';
import { GOALS_TEXT_SCOPE_STYLES, TASK_STATUS_OPTIONS, formatDate, prettyLabel } from './shared';

const surface = {
  card: { border: '1px solid #dbeafe', borderRadius: 20, background: '#ffffff', boxShadow: '0 18px 40px rgba(14, 116, 144, 0.08)' },
  soft: { border: '1px solid #e2e8f0', borderRadius: 16, background: '#f8fafc' },
  info: { border: '1px solid #bfdbfe', borderRadius: 16, background: '#eff6ff' },
};

export default function GoalTasksList({ goal, canCreate = false, adminView = false }) {
  const [tasks, setTasks] = useState([]);
  const [form, setForm] = useState({ task_title: '', task_description: '', due_date: '' });
  const [proofFiles, setProofFiles] = useState({});
  const [proofNotes, setProofNotes] = useState({});
  const [scoreDrafts, setScoreDrafts] = useState({});
  const [uploadState, setUploadState] = useState({});
  const [saveState, setSaveState] = useState({});
  const [taskMessage, setTaskMessage] = useState('');
  const [taskError, setTaskError] = useState('');
  const [proofPreviewUrls, setProofPreviewUrls] = useState({});

  const getAbsoluteProofUrl = (photoUrl) => {
    if (!photoUrl) return '';
    try {
      return new URL(photoUrl, api.defaults.baseURL).toString();
    } catch {
      return photoUrl;
    }
  };

  const loadTasks = async () => {
    try {
      const response = await api.get(`/goals/${goal.id}/tasks`);
      const nextTasks = response.data?.data || [];
      setTasks(nextTasks);
      setProofNotes((prev) => {
        const next = { ...prev };
        nextTasks.forEach((task) => {
          next[task.id] = prev[task.id] ?? task.proof_notes ?? '';
        });
        return next;
      });
      setScoreDrafts((prev) => {
        const next = { ...prev };
        nextTasks.forEach((task) => {
          next[task.id] = prev[task.id] ?? {
            admin_score: task.admin_score ?? '',
            admin_score_notes: task.admin_score_notes || '',
          };
        });
        return next;
      });
    } catch {
      setTasks([]);
    }
  };

  useEffect(() => {
    loadTasks();
  }, [goal.id]);

  useEffect(() => {
    let active = true;
    const objectUrls = [];

    const buildProofPreviews = async () => {
      const nextMap = {};
      for (const task of tasks) {
        const photos = task.proof_photos || [];
        nextMap[task.id] = [];
        for (const photo of photos) {
          const absoluteUrl = getAbsoluteProofUrl(photo.url);
          if (!absoluteUrl) continue;
          try {
            const response = await fetch(absoluteUrl);
            if (!response.ok) continue;
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            objectUrls.push(objectUrl);
            nextMap[task.id].push({
              ...photo,
              absoluteUrl,
              previewUrl: objectUrl,
            });
          } catch {
            nextMap[task.id].push({
              ...photo,
              absoluteUrl,
              previewUrl: absoluteUrl,
            });
          }
        }
      }

      if (active) {
        setProofPreviewUrls(nextMap);
      } else {
        objectUrls.forEach((url) => URL.revokeObjectURL(url));
      }
    };

    if (tasks.length) {
      buildProofPreviews();
    } else {
      setProofPreviewUrls({});
    }

    return () => {
      active = false;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [tasks]);

  const taskStats = useMemo(() => {
    const total = tasks.length;
    const completed = tasks.filter((task) => task.status === 'COMPLETED').length;
    const withProof = tasks.filter((task) => (task.proof_photos || []).length > 0).length;
    return { total, completed, withProof };
  }, [tasks]);

  const createTask = async (event) => {
    event.preventDefault();
    setTaskMessage('');
    setTaskError('');
    if (!form.task_title.trim()) {
      setTaskError('Task title is required.');
      return;
    }
    try {
      await api.post(`/goals/${goal.id}/tasks`, form);
      setForm({ task_title: '', task_description: '', due_date: '' });
      setTaskMessage('Task created successfully.');
      await loadTasks();
    } catch (error) {
      setTaskError(error?.response?.data?.error || 'Failed to create task.');
    }
  };

  const updateTask = async (task, next) => {
    if (adminView) return;
    setSaveState((prev) => ({ ...prev, [task.id]: { type: 'info', text: 'Saving task update...' } }));
    try {
      await api.put(`/goals/tasks/${task.id}`, next);
      setSaveState((prev) => ({ ...prev, [task.id]: { type: 'success', text: 'Task updated.' } }));
      await loadTasks();
    } catch (error) {
      setSaveState((prev) => ({ ...prev, [task.id]: { type: 'error', text: error?.response?.data?.error || 'Task update failed.' } }));
    }
  };

  const uploadProof = async (task) => {
    if (adminView) return;
    const files = proofFiles[task.id] || [];
    if (!files.length) {
      setUploadState((prev) => ({ ...prev, [task.id]: { type: 'error', text: 'Please choose at least one photo first.' } }));
      return;
    }

    const payload = new FormData();
    files.forEach((file) => payload.append('photos', file));
    payload.append('proof_notes', proofNotes[task.id] || '');
    setUploadState((prev) => ({ ...prev, [task.id]: { type: 'info', text: 'Uploading proof photos...' } }));

    try {
      await api.post(`/goals/tasks/${task.id}/proof`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setProofFiles((prev) => ({ ...prev, [task.id]: [] }));
      setUploadState((prev) => ({ ...prev, [task.id]: { type: 'success', text: 'Proof photos uploaded successfully.' } }));
      await loadTasks();
    } catch (error) {
      setUploadState((prev) => ({ ...prev, [task.id]: { type: 'error', text: error?.response?.data?.error || 'Photo upload failed.' } }));
    }
  };

  const saveScore = async (task) => {
    const draft = scoreDrafts[task.id] || {};
    if (draft.admin_score === '' || draft.admin_score == null) {
      setSaveState((prev) => ({ ...prev, [task.id]: { type: 'error', text: 'Enter a score before saving.' } }));
      return;
    }
    setSaveState((prev) => ({ ...prev, [task.id]: { type: 'info', text: 'Saving admin score...' } }));
    try {
      await api.post(`/goals/tasks/${task.id}/score`, {
        admin_score: Number(draft.admin_score),
        admin_score_notes: draft.admin_score_notes || '',
      });
      setSaveState((prev) => ({ ...prev, [task.id]: { type: 'success', text: 'Admin score saved.' } }));
      await loadTasks();
    } catch (error) {
      setSaveState((prev) => ({ ...prev, [task.id]: { type: 'error', text: error?.response?.data?.error || 'Unable to save admin score.' } }));
    }
  };

  const statusTone = (status) => {
    const normalized = String(status || '').toUpperCase();
    if (normalized === 'COMPLETED') return { bg: '#dcfce7', text: '#166534', border: '#86efac' };
    if (normalized === 'IN_PROGRESS') return { bg: '#dbeafe', text: '#1d4ed8', border: '#93c5fd' };
    if (normalized === 'BLOCKED') return { bg: '#fee2e2', text: '#b91c1c', border: '#fca5a5' };
    return { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' };
  };

  const bannerStyle = (type) => {
    if (type === 'success') return { background: '#dcfce7', color: '#166534', border: '1px solid #86efac' };
    if (type === 'error') return { background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5' };
    return { background: '#eff6ff', color: '#1d4ed8', border: '1px solid #93c5fd' };
  };

  return (
    <div className="goals-text-scope" style={{ display: 'grid', gap: 18 }}>
      <style>{GOALS_TEXT_SCOPE_STYLES}</style>

      <div style={{ ...surface.card, padding: 18, background: 'linear-gradient(135deg,#ecfeff 0%,#f8fafc 100%)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ maxWidth: 760 }}>
            <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: '0.12em' }}>{adminView ? 'TASK GOVERNANCE DESK' : 'TASK WISE EXECUTION'}</div>
            <div style={{ marginTop: 8, fontSize: 24, fontWeight: 900 }}>{adminView ? 'Review Module Delivery Without Acting Like A Worker' : 'Track Work, Upload Proof, Review Clearly'}</div>
            <div style={{ marginTop: 8, fontSize: 14, lineHeight: 1.6 }}>
              {adminView
                ? 'Admin sees task progress, proof, and quality scores across all modules. Execution edits stay with the assigned teams so the admin workspace remains supervisory.'
                : 'Each task has its own progress, proof photos, and review area. Teams can upload photo proof task-wise, and Admin can score the proof from the same screen.'}
            </div>
          </div>
          <div style={{ ...surface.info, padding: '12px 16px', minWidth: 200 }}>
            <div style={{ fontSize: 12, fontWeight: 800 }}>Goal</div>
            <div style={{ marginTop: 4, fontSize: 16, fontWeight: 900 }}>{goal.title}</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12, marginTop: 18 }}>
          <div style={{ ...surface.soft, padding: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 800 }}>Total Tasks</div>
            <div style={{ marginTop: 6, fontSize: 28, fontWeight: 900 }}>{taskStats.total}</div>
          </div>
          <div style={{ ...surface.soft, padding: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 800 }}>Completed Tasks</div>
            <div style={{ marginTop: 6, fontSize: 28, fontWeight: 900 }}>{taskStats.completed}</div>
          </div>
          <div style={{ ...surface.soft, padding: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 800 }}>Tasks With Proof</div>
            <div style={{ marginTop: 6, fontSize: 28, fontWeight: 900 }}>{taskStats.withProof}</div>
          </div>
        </div>
      </div>

      {canCreate ? (
        <form onSubmit={createTask} style={{ ...surface.card, padding: 18, display: 'grid', gap: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontWeight: 900, fontSize: 18 }}>Create New Task</div>
              <div style={{ marginTop: 4, fontSize: 13 }}>Start with a task first, then upload photo proof inside that task card.</div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1.3fr 220px', gap: 12 }}>
            <input value={form.task_title} onChange={(e) => setForm({ ...form, task_title: e.target.value })} placeholder="Task title" style={{ padding: 12, borderRadius: 12, border: '1px solid #cbd5e1' }} />
            <textarea rows="2" value={form.task_description} onChange={(e) => setForm({ ...form, task_description: e.target.value })} placeholder="Task description" style={{ padding: 12, borderRadius: 12, border: '1px solid #cbd5e1', resize: 'vertical' }} />
            <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} style={{ padding: 12, borderRadius: 12, border: '1px solid #cbd5e1' }} />
          </div>
          {taskMessage ? <div style={{ ...bannerStyle('success'), borderRadius: 12, padding: '10px 12px', fontWeight: 700 }}>{taskMessage}</div> : null}
          {taskError ? <div style={{ ...bannerStyle('error'), borderRadius: 12, padding: '10px 12px', fontWeight: 700 }}>{taskError}</div> : null}
          <button type="submit" style={{ border: 0, borderRadius: 12, background: '#0f766e', color: '#fff', padding: '12px 16px', fontWeight: 800, width: 'fit-content' }}>
            Add Task
          </button>
        </form>
      ) : null}

      <div style={{ display: 'grid', gap: 14 }}>
        {tasks.length ? tasks.map((task) => {
          const tone = statusTone(task.status);
          const files = proofFiles[task.id] || [];
          const saveBanner = saveState[task.id];
          const uploadBanner = uploadState[task.id];

          return (
            <div key={task.id} style={{ ...surface.card, padding: 18, display: 'grid', gap: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ maxWidth: 760 }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ fontWeight: 900, fontSize: 18 }}>{task.task_title}</div>
                    <span style={{ padding: '6px 10px', borderRadius: 999, background: tone.bg, color: tone.text, border: `1px solid ${tone.border}`, fontSize: 12, fontWeight: 800 }}>
                      {prettyLabel(task.status)}
                    </span>
                  </div>
                  <div style={{ marginTop: 6, fontSize: 14, lineHeight: 1.6 }}>{task.task_description || 'No description added for this task.'}</div>
                </div>
                <div style={{ ...surface.info, padding: 12, minWidth: 220 }}>
                  <div style={{ fontSize: 12, fontWeight: 800 }}>Deadline</div>
                  <div style={{ marginTop: 4, fontWeight: 900 }}>{formatDate(task.due_date)}</div>
                  <div style={{ marginTop: 8, fontSize: 12, fontWeight: 800 }}>Completion</div>
                  <div style={{ marginTop: 4, fontWeight: 900 }}>{task.completion_percentage || 0}%</div>
                </div>
              </div>

              {adminView ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
                  <div style={{ ...surface.soft, padding: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 800 }}>Task Status</div>
                    <div style={{ marginTop: 6, fontSize: 18, fontWeight: 900 }}>{prettyLabel(task.status)}</div>
                  </div>
                  <div style={{ ...surface.soft, padding: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 800 }}>Completion</div>
                    <div style={{ marginTop: 6, fontSize: 18, fontWeight: 900 }}>{task.completion_percentage || 0}%</div>
                  </div>
                  <div style={{ ...surface.soft, padding: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 800 }}>Admin Role</div>
                    <div style={{ marginTop: 6, fontSize: 14, lineHeight: 1.5, fontWeight: 700 }}>Review progress, inspect proof, and score quality.</div>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr 90px', gap: 12, alignItems: 'center' }}>
                    <select value={task.status} onChange={(e) => updateTask(task, { status: e.target.value, completion_percentage: task.completion_percentage })} style={{ padding: 12, borderRadius: 12, border: '1px solid #cbd5e1' }}>
                      {TASK_STATUS_OPTIONS.map((option) => <option key={option} value={option}>{prettyLabel(option)}</option>)}
                    </select>
                    <div style={{ display: 'grid', gap: 8 }}>
                      <input type="range" min="0" max="100" value={task.completion_percentage || 0} onChange={(e) => updateTask(task, { status: task.status, completion_percentage: Number(e.target.value) })} />
                      <div style={{ height: 10, borderRadius: 999, background: '#e2e8f0', overflow: 'hidden' }}>
                        <div style={{ width: `${task.completion_percentage || 0}%`, height: '100%', background: 'linear-gradient(90deg,#38bdf8 0%,#2563eb 100%)' }} />
                      </div>
                    </div>
                    <div style={{ fontWeight: 900, fontSize: 18, color: '#2563eb', textAlign: 'right' }}>{task.completion_percentage || 0}%</div>
                  </div>

                  {saveBanner ? <div style={{ ...bannerStyle(saveBanner.type), borderRadius: 12, padding: '10px 12px', fontWeight: 700 }}>{saveBanner.text}</div> : null}
                </>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: adminView ? '1.25fr 0.75fr' : '1fr', gap: 16 }}>
                <div style={{ ...surface.soft, padding: 16, display: 'grid', gap: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontWeight: 900, fontSize: 16 }}>{adminView ? 'Submitted Proof Review' : 'Photo Proof Area'}</div>
                      <div style={{ marginTop: 4, fontSize: 13 }}>{adminView ? 'Proof submitted by module teams appears here for review.' : 'Upload task-wise photos so Admin can verify department work visually.'}</div>
                    </div>
                    <div style={{ padding: '6px 10px', borderRadius: 999, background: '#e0f2fe', border: '1px solid #7dd3fc', color: '#0369a1', fontWeight: 800 }}>
                      {(task.proof_photos || []).length} Photos
                    </div>
                  </div>

                  {adminView ? (
                    <div style={{ ...surface.info, padding: 14, display: 'grid', gap: 8 }}>
                      <div style={{ fontWeight: 800 }}>Proof Notes</div>
                      <div style={{ fontSize: 14, lineHeight: 1.6 }}>{task.proof_notes || 'No proof note has been submitted yet.'}</div>
                    </div>
                  ) : (
                    <>
                      <textarea
                        rows="3"
                        value={proofNotes[task.id] ?? task.proof_notes ?? ''}
                        onChange={(e) => setProofNotes((prev) => ({ ...prev, [task.id]: e.target.value }))}
                        placeholder="Describe what these proof photos show in simple business language"
                        style={{ padding: 12, borderRadius: 12, border: '1px solid #cbd5e1', resize: 'vertical' }}
                      />

                      <div style={{ ...surface.info, padding: 14, display: 'grid', gap: 10 }}>
                        <div style={{ fontWeight: 800 }}>Choose Proof Photos</div>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={(e) => setProofFiles((prev) => ({ ...prev, [task.id]: Array.from(e.target.files || []) }))}
                        />
                        {files.length ? (
                          <div style={{ display: 'grid', gap: 6 }}>
                            {files.map((file) => (
                              <div key={`${task.id}-${file.name}-${file.size}`} style={{ fontSize: 13, fontWeight: 700 }}>
                                {file.name}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{ fontSize: 13 }}>No new photos selected yet.</div>
                        )}
                        <button type="button" onClick={() => uploadProof(task)} style={{ border: 0, borderRadius: 12, background: '#1d4ed8', color: '#fff', padding: '12px 16px', fontWeight: 800, width: 'fit-content' }}>
                          Upload Proof Photos
                        </button>
                      </div>

                      {uploadBanner ? <div style={{ ...bannerStyle(uploadBanner.type), borderRadius: 12, padding: '10px 12px', fontWeight: 700 }}>{uploadBanner.text}</div> : null}
                    </>
                  )}

                  {(proofPreviewUrls[task.id] || []).length ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                      {(proofPreviewUrls[task.id] || []).map((photo, index) => (
                        <a key={`${task.id}-${index}`} href={photo.absoluteUrl || photo.url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
                          <div style={{ border: '1px solid #bfdbfe', borderRadius: 16, overflow: 'hidden', background: '#ffffff' }}>
                            <img src={photo.previewUrl || photo.absoluteUrl || photo.url} alt={photo.name || `Proof ${index + 1}`} style={{ width: '100%', height: 150, objectFit: 'cover', display: 'block' }} />
                            <div style={{ padding: 12 }}>
                              <div style={{ fontSize: 12, fontWeight: 900 }}>{photo.name || `Proof ${index + 1}`}</div>
                              <div style={{ marginTop: 6, fontSize: 12 }}>Uploaded: {formatDate(photo.uploaded_at)}</div>
                            </div>
                          </div>
                        </a>
                      ))}
                    </div>
                  ) : (
                    <div style={{ ...surface.soft, padding: 14, fontSize: 13 }}>
                      {adminView ? 'No proof photos have been submitted for this task yet.' : <>No proof photos uploaded yet. Create or select a task first, then choose photos and click <strong>Upload Proof Photos</strong>.</>}
                    </div>
                  )}
                </div>

                {adminView ? (
                  <div style={{ ...surface.info, padding: 16, display: 'grid', gap: 12 }}>
                    <div>
                      <div style={{ fontWeight: 900, fontSize: 16 }}>Admin Review & Score</div>
                      <div style={{ marginTop: 4, fontSize: 13 }}>Review the proof, add comments, and give a score out of 10.</div>
                    </div>
                    <input
                      type="number"
                      min="0"
                      max="10"
                      step="0.1"
                      value={scoreDrafts[task.id]?.admin_score ?? ''}
                      onChange={(e) => setScoreDrafts((prev) => ({
                        ...prev,
                        [task.id]: {
                          ...(prev[task.id] || {}),
                          admin_score: e.target.value,
                        },
                      }))}
                      placeholder="Score 0 to 10"
                      style={{ padding: 12, borderRadius: 12, border: '1px solid #cbd5e1' }}
                    />
                    <textarea
                      rows="4"
                      value={scoreDrafts[task.id]?.admin_score_notes ?? ''}
                      onChange={(e) => setScoreDrafts((prev) => ({
                        ...prev,
                        [task.id]: {
                          ...(prev[task.id] || {}),
                          admin_score_notes: e.target.value,
                        },
                      }))}
                      placeholder="Write what was good, what is missing, and what the team should improve"
                      style={{ padding: 12, borderRadius: 12, border: '1px solid #cbd5e1', resize: 'vertical' }}
                    />
                    <div style={{ ...surface.soft, padding: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 800 }}>Current Saved Score</div>
                      <div style={{ marginTop: 6, fontSize: 22, fontWeight: 900 }}>{task.admin_score ?? 'Not scored yet'}</div>
                      <div style={{ marginTop: 6, fontSize: 13 }}>{task.admin_score_notes || 'No admin review comment saved yet.'}</div>
                    </div>
                    <button type="button" onClick={() => saveScore(task)} style={{ border: 0, borderRadius: 12, background: '#0f766e', color: '#fff', padding: '12px 16px', fontWeight: 800 }}>
                      Save Admin Score
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          );
        }) : (
          <div style={{ ...surface.card, padding: 18 }}>
            <div style={{ fontWeight: 900, fontSize: 18 }}>No Tasks Added Yet</div>
            <div style={{ marginTop: 8, fontSize: 14, lineHeight: 1.6 }}>
              This goal does not have any task cards yet. Start by creating a task. Once a task exists, that task will show its own proof-photo upload area and review section.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
